import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle, CheckCircle2, Clock, Loader2, ShieldCheck, XCircle,
} from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";

const DIGIO_SDK_URL = "https://app.digio.in/sdk/v11/digio.js";

function loadDigioSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.Digio) return Promise.resolve(window.Digio);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-digio-sdk]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Digio));
      existing.addEventListener("error", () => reject(new Error("Failed to load Digio SDK")));
      return;
    }
    const script = document.createElement("script");
    script.src = DIGIO_SDK_URL;
    script.async = true;
    script.dataset.digioSdk = "1";
    script.onload = () => resolve(window.Digio);
    script.onerror = () => reject(new Error("Failed to load Digio SDK"));
    document.body.appendChild(script);
  });
}

function buildRedirectUrl() {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/kyc/digio/callback?from=kyc`;
}

function Banner({ kycStatus, digioStatus, reason }) {
  if (kycStatus === "approved") {
    return (
      <div className="flex items-start gap-3 rounded-[14px] border px-5 py-4 bg-emerald-50 text-emerald-700 border-emerald-200">
        <CheckCircle2 size={18} className="mt-0.5" />
        <div className="text-[14px] font-medium">KYC approved via Digio — you can start trading.</div>
      </div>
    );
  }
  if (kycStatus === "pending_review") {
    return (
      <div className="flex items-start gap-3 rounded-[14px] border px-5 py-4 bg-amber-50 text-amber-700 border-amber-200">
        <Clock size={18} className="mt-0.5" />
        <div className="text-[14px] font-medium">KYC under review.</div>
      </div>
    );
  }
  if (kycStatus === "rejected") {
    return (
      <div className="flex items-start gap-3 rounded-[14px] border px-5 py-4 bg-red-50 text-red-700 border-red-200">
        <XCircle size={18} className="mt-0.5" />
        <div className="text-[14px] font-medium">KYC rejected: {reason || "please try again"}</div>
      </div>
    );
  }
  const hint =
    digioStatus === "pending"
      ? "Digio verification in progress — finish in the Digio window, then refresh status."
      : digioStatus === "rejected"
        ? "Digio rejected this attempt. Restart KYC to try again."
        : "Verify your identity with Digio (Aadhaar / DigiLocker workflow).";
  return (
    <div className="flex items-start gap-3 rounded-[14px] border px-5 py-4 bg-blue-50 text-cm-blue border-blue-200">
      <ShieldCheck size={18} className="mt-0.5" />
      <div className="text-[14px] font-medium">{hint}</div>
    </div>
  );
}

export default function DigioKycFlow() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null);
  const [status, setStatus] = useState(null);
  const [digio, setDigio] = useState(null);
  const digioRef = useRef(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [{ data: m }, { data: s }] = await Promise.all([
        api.get("/api/kyc/mode"),
        api.get("/api/kyc/status"),
      ]);
      setMode(m);
      setStatus(s);
      setDigio(s.digio || m.digio || null);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const syncStatus = useCallback(async () => {
    const { data } = await api.post("/api/kyc/digio/sync", {});
    if (data.digio) setDigio(data.digio);
    await load();
    return data;
  }, [load]);

  const openDigioSdk = useCallback(
    async (session) => {
      const DigioCtor = await loadDigioSdk();
      if (!DigioCtor) throw new Error("Digio SDK unavailable");

      const redirectUrl = buildRedirectUrl();
      const environment =
        session.environment === "production" || mode?.digio_environment === "production"
          ? "production"
          : "sandbox";

      await new Promise((resolve, reject) => {
        try {
          if (digioRef.current?.cancel) {
            try {
              digioRef.current.cancel();
            } catch {
              /* ignore */
            }
          }

          let settled = false;
          const settleOk = async (response) => {
            if (settled) return;
            settled = true;
            try {
              await syncStatus();
              resolve(response);
            } catch (e) {
              reject(e);
            }
          };
          const settleErr = (msg) => {
            if (settled) return;
            settled = true;
            reject(new Error(msg || "Digio KYC was cancelled or failed"));
          };

          const instance = new DigioCtor({
            environment,
            is_redirection_approach: true,
            redirect_url: redirectUrl,
            callback: async (response) => {
              if (response?.error_code || response?.error) {
                settleErr(
                  response.error_message ||
                    response.message ||
                    response.error ||
                    "Digio KYC was cancelled or failed"
                );
                return;
              }
              await settleOk(response);
            },
            logo:
              typeof window !== "undefined"
                ? `${window.location.origin}/favicon.ico`
                : undefined,
            theme: {
              primaryColor: "#1D4ED8",
              secondaryColor: "#0B1220",
            },
          });
          digioRef.current = instance;
          instance.init();
          instance.submit(session.kid, session.customerIdentifier, {
            token: session.accessToken?.id,
          });

          setTimeout(() => {
            if (!settled) resolve({ redirected: true });
          }, 2500);
        } catch (e) {
          reject(e);
        }
      });
    },
    [mode?.digio_environment, syncStatus]
  );

  async function startSession(restart = false) {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        restart ? "/api/kyc/digio/restart" : "/api/kyc/digio/start",
        restart ? { restart: true } : {}
      );
      if (data.alreadyApproved) {
        await load();
        return;
      }
      const session = data.digio;
      if (!session?.kid || !session?.accessToken?.id) {
        throw new Error("Digio session incomplete — check DIGIO_* env and template name.");
      }
      setDigio(session);
      await openDigioSdk(session);
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="text-cm-grey text-[13px] inline-flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading Digio KYC…
      </div>
    );
  }

  if (mode?.kyc_mode === "disabled" || mode?.kyc_enabled === false) {
    return (
      <div className="rounded-[16px] border border-[var(--cm-border)] bg-white p-6 text-[14px] text-cm-grey">
        KYC verification is currently disabled. Contact support.
      </div>
    );
  }

  if (!mode?.digio_configured) {
    return (
      <div className="rounded-[16px] border border-amber-200 bg-amber-50 px-5 py-4 text-[14px] text-amber-800">
        Digio KYC is selected but not configured. Add{" "}
        <strong>DIGIO_CLIENT_ID</strong>, <strong>DIGIO_CLIENT_SECRET</strong>, and{" "}
        <strong>DIGIO_TEMPLATE_NAME</strong> on the server.
      </div>
    );
  }

  const kycStatus = status?.kyc_status;
  const digioStatus = digio?.status || status?.digio_status;
  const approved = kycStatus === "approved";

  return (
    <div className="space-y-4">
      <Banner
        kycStatus={kycStatus}
        digioStatus={digioStatus}
        reason={status?.rejection_reason}
      />

      {!approved ? (
        <div className="rounded-[16px] border border-[var(--cm-border)] bg-white p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-[12px] bg-cm-lavender grid place-items-center shrink-0">
              <ShieldCheck size={18} className="text-cm-blue" />
            </div>
            <div>
              <h3 className="text-[16px] font-bold text-cm-navy">Automatic KYC (Digio)</h3>
              <p className="text-[13px] text-cm-grey mt-1 leading-relaxed">
                You will be taken to Digio to complete verification, then returned here
                automatically. Use Restart if a previous attempt is stuck.
              </p>
            </div>
          </div>

          {digio?.kid && digioStatus === "pending" ? (
            <p className="text-[12px] text-cm-grey">
              In progress — request{" "}
              <span className="font-mono text-cm-navy">{digio.kid}</span>
            </p>
          ) : null}

          {err ? (
            <div className="flex items-start gap-2 text-[13px] text-red-600">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              {err}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => startSession(false)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[12px] bg-cm-blue text-white text-[13px] font-semibold disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              {digio?.kid && digioStatus === "pending" ? "Continue Digio KYC" : "Start Digio KYC"}
            </button>
            {digio?.kid ? (
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setErr("");
                  try {
                    await syncStatus();
                  } catch (e) {
                    setErr(getErrorMessage(e));
                  } finally {
                    setBusy(false);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[12px] border border-[var(--cm-border)] text-[13px] font-semibold text-cm-navy disabled:opacity-50"
              >
                Refresh status
              </button>
            ) : null}
            {digio?.kid ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => startSession(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[12px] border border-[var(--cm-border)] text-[13px] font-medium text-cm-grey disabled:opacity-50"
              >
                Restart KYC
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
